import {
  Text,
  Spinner,
  Badge,
  Button,
  GreyGrey30,
  GreyGrey80,
  GreyGrey90,
  IconPlus,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  Primary14SemiBold,
  ShadowXs,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { toQuoteStatusBadgeProps } from '@sequencehq/utils'
import { useCustomerContext } from 'Customer/hooks/useCustomerContext'
import { useGetQuotes } from 'Customer/hooks/useGetQuotes'
import { ProtectedComponent } from 'lib/rbac'
import { useNavigate } from 'react-router-dom'

export const QuoteTable = () => {
  const navigate = useNavigate()
  const { customer } = useCustomerContext()
  const { quotes, isLoading } = useGetQuotes(customer.id, 12)

  if (isLoading) {
    return (
      <Center height="100%" width="100%" mt={4}>
        <Spinner />
      </Center>
    )
  }

  const isArchived = Boolean(customer.archivedAt)

  return (
    <Box p={4}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text {...Primary14SemiBold}>Quotes</Text>
        <ProtectedComponent permission="org.quote:create">
          <Button
            {...Primary13SemiBold}
            variant="ghost"
            leadingIcon={
              <IconPlus color={isArchived ? undefined : IndigoIndigo60} />
            }
            disabled={isArchived}
            {...(!isArchived && {
              as: Link.Unstyled,
              to: `/quotes/new?customerId=${customer.id}`
            })}
          >
            Create quote
          </Button>
        </ProtectedComponent>
      </Flex>
      <TableContainer
        borderRadius="lg"
        overflow="hidden"
        border={`1px solid ${GreyGrey30}`}
        mt={2}
        width="100%"
        boxShadow={ShadowXs}
      >
        <Table variant="v2" width="100%">
          <Thead>
            <Tr>
              <Th>Title</Th>
              <Th width={120}>Number</Th>
              <Th width={120}>Status</Th>
              <Th width={200}>Owner</Th>
              <Th width={200}>Expires</Th>
            </Tr>
          </Thead>
          <Tbody>
            {quotes.length > 0 ? (
              quotes.map(quote => {
                return (
                  <Tr
                    key={quote.id}
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                  >
                    <Td>
                      <Text
                        {...Primary13Regular}
                        as={Link.Unstyled}
                        to={`/quotes/${quote.id}`}
                        color={IndigoIndigo60}
                        onClick={e => e.stopPropagation()}
                      >
                        {quote.title}
                      </Text>
                    </Td>
                    <Td>{quote.quoteNumber}</Td>
                    <Td>
                      <Badge
                        {...toQuoteStatusBadgeProps({
                          status: quote.status,
                          isPublished: Boolean(quote.publishedAt),
                          hasApprovalWorkflow: Boolean(quote.approvalWorkflowId)
                        })}
                      />
                    </Td>
                    <Td>
                      <QuoteOwner createdBy={quote.createdBy} />
                    </Td>
                    <Td>
                      {quote.expiresAt
                        ? dateTimeWithFormat(quote.expiresAt, 'd MMM yyyy')
                        : quote.expiryDays
                          ? `${quote.expiryDays} days after publishing`
                          : ''}
                    </Td>
                  </Tr>
                )
              })
            ) : (
              <Tr
                sx={{
                  cursor: 'auto!important',
                  _hover: {
                    bgColor: 'inherit!important'
                  }
                }}
              >
                <Td colSpan={5}>
                  <EmptyContent />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}

const EmptyContent = () => {
  return (
    <Box width="400px" margin="12px auto" textAlign="center">
      <Text {...Primary13SemiBold} color={GreyGrey90}>
        No quotes to display
      </Text>
      <Text {...Primary13Regular} color={GreyGrey80}>
        You have not created a quote for this customer yet.
      </Text>
    </Box>
  )
}

const QuoteOwner = ({ createdBy }: { createdBy: string }) => {
  const userQuery = useQuery(dashboard20240730Client.getUser, {
    id: createdBy
  })

  if (userQuery.isPending || !userQuery.data) {
    return <Skeleton width="100px" height="20px" />
  }

  return <>{userQuery.data?.email}</>
}
